// libraries
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import Dinero from 'dinero.js'
import noop from 'lodash/noop'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useForm, FormProvider, useFieldArray } from 'react-hook-form'
import { Stack, Grid, Box, Divider, Typography, Button, IconButton } from '@mui/material_modern'
import { Add } from '@mui/icons-material_modern'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import DeleteIcon from '@mui/icons-material_modern/Delete'

// components
import Input from 'components/generic/form/Input'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import FormModalActionBar from 'components/generic/modals/form-modal/FormModalActionBar'
import Loading from 'components/generic/Loading'
import DateTimePicker from 'components/generic/form/DateTimePicker'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import BasicDataGrid from 'components/generic/datagrid'
import MaterialAutocomplete from 'components/generic/form/AsyncAutocomplete/MaterialAutocomplete'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import EditableCell from 'components/generic/datagrid/cells/EditableCell'
import { useCustomDataGridStyles } from 'components/containers/load-board/utils/useCustomStyles'

// state
import { setDelayRefetch } from 'state/inventory'

// services
import {
  useCreateInventoryAdjustmentMutation,
  useGetInventoryAdjustmentQuery,
  useDeleteInventoryAdjustmentMutation,
  useUpdateInventoryAdjustmentMutation,
} from 'services/inventoryAdjustments'

import { useGetInventoryMaterialTypeByIdsMutation } from 'services/inventory'

// helpers and utils
import { formOptions } from 'utils/validation/adjustments'
import { MODAL_VIEW_MODES, CHURN_ZERO_EVENTS as cz, PAGE_SIZE, ROUNDING_PRECISIONS } from 'utils/constants'
import { trackEvent } from 'utils/tracking'
import {
  dispatchSnackbar,
  formatNumberForDisplay,
  parseNumberObject,
  setDecimalPrecision,
  checkIfDateIsLocked,
} from 'utils/helpers'
import FORM_MODALS from 'utils/ui/modals'
import { showFormModal } from 'state/ui'
import { roundTo } from 'utils/data-manipulation/valueFormatter'
import { formatData, getNewMaterial } from './utils'

const NewAdjustmentContainer = ({ onFormStateChange, onHideModal }) => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const classes = useCustomDataGridStyles({})
  const apiRef = useGridApiRef()
  const { viewMode, navigateTo, initialMaterialTypeId, initialEffectiveDate, initialNewWeight } = useSelector(
    state => state.ui.formModalProps,
  )
  const [showCloseModal, setShowCloseModal] = useState(false)
  const [showVoidModal, setShowVoidModal] = useState(false)
  const [isFetchingInventory, setIsFetchingInventory] = useState(false)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const { enableFourDecimalPrecisionInInventory } = useSelector(state => state.featureFlags?.variables || {})
  const { defaultYardTimezone } = useSelector(state => state.auth)
  const isNew = viewMode === MODAL_VIEW_MODES.NEW

  const { defaultYardUm, settings } = useSelector(state => state.settings)
  const { lockAccountingPeriod } = settings?.overviewSettings?.overview || {}

  const methods = useForm({
    ...formOptions,
    mode: 'onTouched',
    reValidateMode: 'onBlur',
    defaultValues: {
      effectiveDate: dayjs(),
      materials: [getNewMaterial()],
      notes: '',
    },
  })

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty, touchedFields, dirtyFields },
    watch,
    trigger,
    setValue,
    getValues,
    clearErrors,
    setError,
  } = methods
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'materials',
  })
  const watchFieldArray = watch('materials')
  const watchEffectiveDate = watch('effectiveDate')
  const controlledFields = fields.map((field, index) => ({
    ...field,
    ...watchFieldArray[index],
    index,
  }))

  const adjustmentId = useMemo(() => {
    const cleanedPathname = pathname.at(-1) === '/' ? pathname.slice(0, -1) : pathname
    return isNew ? null : cleanedPathname.split('/').pop()
  }, [pathname])

  const { data: adjustmentData, isLoading: isLoadingGetInventory } = useGetInventoryAdjustmentQuery(
    { id: adjustmentId },
    { skip: isNew },
  )

  const [createInventoryAdjustment, { isLoading: isLoadingCreateAdjustment }] = useCreateInventoryAdjustmentMutation()

  const [deleteAdjustment, { isLoading: isLoadingDeleteAdjustment }] = useDeleteInventoryAdjustmentMutation()

  const [updateAdjustment, { isLoading: isLoadingUpdateAdjustment }] = useUpdateInventoryAdjustmentMutation()

  const [getInventoryMaterialTypeByIds] = useGetInventoryMaterialTypeByIdsMutation()

  const isLoading =
    isLoadingCreateAdjustment ||
    isLoadingDeleteAdjustment ||
    isLoadingUpdateAdjustment ||
    isLoadingGetInventory ||
    isFetchingInventory

  const isInLockedAccountingPeriodV2 = useMemo(() => {
    if (!adjustmentData || !lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) return false
    return checkIfDateIsLocked(lockAccountingPeriod, adjustmentData.effectiveDate)
  }, [lockAccountingPeriod, adjustmentData])

  const initialGridValues = () => watchFieldArray?.length === 1 && watchFieldArray[0]?.materialType === null

  const clearMaterialRow = rowIndex => {
    const emptyMaterial = getNewMaterial()
    setValue(`materials.${rowIndex}.materialType`, emptyMaterial.materialType, {
      shouldDirty: true,
    })
    setValue(`materials.${rowIndex}.materialTypeId`, emptyMaterial.materialTypeId)
    setValue(`materials.${rowIndex}.wip`, emptyMaterial.wip)
    setValue(`materials.${rowIndex}.wipCost`, {
      amount: emptyMaterial.wipCost,
      precision: 4,
    })
    setValue(`materials.${rowIndex}.wipAvgCost`, emptyMaterial.wipAvgCost)
    setValue(`materials.${rowIndex}.newWip`, emptyMaterial.newWip, {
      shouldDirty: true,
    })
    setValue(`materials.${rowIndex}.newWipCost`, emptyMaterial.newWipCost, {
      shouldDirty: true,
    })
    setValue(`materials.${rowIndex}.wipChange`, emptyMaterial.wipChange)
    setValue(`materials.${rowIndex}.wipCostChange`, emptyMaterial.wipCostChange)
    setValue(`materials.${rowIndex}.wipAvgCostChange`, emptyMaterial.wipAvgCostChange)
  }

  const onModalClose = useCallback(() => {
    onHideModal()
    if (navigateTo) {
      navigate(navigateTo)
    }
  })

  const getCzEventName = isVoid => {
    if (isVoid) return cz.VOID_ADJUSTMENT
    if (viewMode === MODAL_VIEW_MODES.NEW) return cz.CREATE_ADJUSTMENT
    if (viewMode === MODAL_VIEW_MODES.EDIT) return cz.UPDATE_ADJUSTMENT
    return cz.INVENTORY_ADJUSTMENTS
  }

  const handleError = (error, action) => {
    trackEvent({
      eventName: getCzEventName(action === 'voiding'),
      errorMsg: error,
    })
    dispatchSnackbar(dispatch, 'error', error?.data?.error || `There was an error ${action} the adjustment`)
    dispatch(setDelayRefetch(false))
  }

  const handleSuccess = (response, action) => {
    const isVoid = action === 'voided'
    trackEvent({
      eventName: getCzEventName(isVoid),
      ...(!isVoid && { id: response?.id }),
    })
    dispatchSnackbar(dispatch, 'success', `Adjustment successfully ${action}`)
    setTimeout(() => {
      dispatch(setDelayRefetch(false))
    }, 3000)
    onModalClose()
  }

  const onClickVoid = async () => {
    setShowVoidModal(false)
    await deleteAdjustment({ id: adjustmentId })
      .unwrap()
      .then(response => handleSuccess(response, 'voided'))
      .catch(error => handleError(error, 'voiding'))
  }

  const onEdit = () => {
    dispatch(
      showFormModal({
        formModalName: FORM_MODALS.NEW_ADJUSTMENT,
        formModalProps: {
          viewMode: MODAL_VIEW_MODES.EDIT,
          navigateTo: '/app/inventory/adjustments',
        },
      }),
    )
  }

  useEffect(() => {
    onFormStateChange(false)
  }, [])

  const fetchMaterialsInventory = async (callback, materialTypeIds, effectiveDateValue) => {
    const { data, error } = await getInventoryMaterialTypeByIds({
      body: materialTypeIds,
      filter: JSON.stringify({ effectiveDate: dayjs(effectiveDateValue)?.toISOString() }),
    })
    if (error) {
      handleError(error, 'fetching')
    }
    callback(data || [])
  }

  useEffect(() => {
    if (isNew && initialMaterialTypeId && initialEffectiveDate && initialNewWeight) {
      setValue('effectiveDate', dayjs(initialEffectiveDate), { shouldDirty: true })
      setIsFetchingInventory(true)
      fetchMaterialsInventory(
        materialsInventory => {
          setIsFetchingInventory(false)
          const materialInventory = materialsInventory[0]
          const emptyMaterial = getNewMaterial()
          const wip = Number(materialInventory?.wip?.amount) || 0
          const wipCost = parseNumberObject(materialInventory?.wipCost, {
            format: 'none',
            enableFourDecimalPrecisionInInventory,
          })

          const wipAvgCost = parseNumberObject(materialInventory?.wipAvgCost, {
            format: 'none',
            enableFourDecimalPrecisionInInventory,
          })

          const newMaterial = {
            ...emptyMaterial,
            materialTypeId: initialMaterialTypeId,
            materialType: {
              ...emptyMaterial?.materialType,
              materialTypeId: initialMaterialTypeId,
              code: materialInventory?.materialTypeCode,
            },
            wip,
            wipCost: materialInventory?.wipCost,
            wipAvgCost: roundTo(wipAvgCost, ROUNDING_PRECISIONS.PRICE),
            newWip: initialNewWeight + Number(materialInventory?.wip?.amount),
            newWipCost: wip === 0 ? 0 : parseFloat(wipCost / wip),
            wipChange: initialNewWeight,
            wipCostChange: roundTo(0, ROUNDING_PRECISIONS.PRICE),
            wipAvgCostChange: roundTo(0, ROUNDING_PRECISIONS.PRICE),
          }
          setValue('materials', [newMaterial], { shouldDirty: true })
        },
        [initialMaterialTypeId],
        dayjs(initialEffectiveDate),
      )
    }
  }, [initialMaterialTypeId, initialEffectiveDate, initialNewWeight])

  useEffect(() => {
    // THIS WILL RUN ONCE WHEN THE COMPONENT MOUNTS AND WHEN THE ADJUSTMENT DATA CHANGES
    // 1. IF THERE IS NO ADJUSTMENT DATA OR THE VIEW MODE IS NOT VIEW, RETURN
    // 2. IF watchEffectiveDate ALREADY MATCHES THE ADJUSTMENT DATA, RETURN (DON'T NEED TO FETCH AGAIN)
    // 3. FETCH MATERIALS INVENTORY
    // 4. RESET FORM WITH INITIAL DATA
    if (!adjustmentData || viewMode !== MODAL_VIEW_MODES.VIEW) return
    if (watchEffectiveDate.toISOString() === adjustmentData.effectiveDate) return

    const materialTypeIds = adjustmentData?.materials?.map(material => material?.materialTypeId)

    const validDateTime = dayjs(adjustmentData.effectiveDate).isValid()
    if (!validDateTime) return

    setIsFetchingInventory(true)
    fetchMaterialsInventory(
      materialsInventory => {
        setIsFetchingInventory(false)
        const newMaterials = adjustmentData?.materials?.map(adjustmentMaterial => {
          const emptyMaterial = getNewMaterial()
          const materialInventory = materialsInventory.find(
            entry => entry.materialTypeId === adjustmentMaterial?.materialTypeId,
          )
          const wip = Number(materialInventory?.wip?.amount) || 0
          const wipCost = parseNumberObject(materialInventory?.wipCost, {
            format: 'none',
            enableFourDecimalPrecisionInInventory,
          })
          const netCost = adjustmentMaterial?.netCost || 0
          const netWeight = adjustmentMaterial?.netWeight || 0
          const costTotal = roundTo(netCost + wipCost, ROUNDING_PRECISIONS.COST)
          const weightTotal = netWeight + wip

          const wipAvgCost = parseNumberObject(materialInventory?.wipAvgCost, {
            format: 'none',
            enableFourDecimalPrecisionInInventory,
          })

          const newWip = materialInventory?.wip?.amount - netWeight * '-1.0000'

          const newWipCost = weightTotal === 0 ? 0 : parseFloat(costTotal / weightTotal)
          const wipAvgCostChange = newWipCost - wipAvgCost

          const newMaterial = {
            ...emptyMaterial,
            id: adjustmentMaterial?.id,
            materialTypeId: adjustmentMaterial?.materialTypeId,
            materialType: {
              ...adjustmentMaterial?.materialType,
              materialTypeId: adjustmentMaterial?.materialTypeId,
              code: materialInventory?.materialTypeCode,
            },
            wip,
            wipCost: materialInventory?.wipCost,
            wipAvgCost: roundTo(wipAvgCost, ROUNDING_PRECISIONS.PRICE),
            newWip,
            newWipCost,
            wipChange: adjustmentMaterial?.netWeight || 0,
            wipCostChange: roundTo(
              newWipCost * newWip - Dinero({ ...materialInventory?.wipCost }).toUnit(),
              ROUNDING_PRECISIONS.PRICE,
            ),
            wipAvgCostChange: roundTo(wipAvgCostChange, ROUNDING_PRECISIONS.PRICE),
          }
          return newMaterial
        })
        reset({ ...adjustmentData, effectiveDate: dayjs(adjustmentData.effectiveDate), materials: newMaterials })
      },
      materialTypeIds,
      adjustmentData?.effectiveDate,
    )
  }, [adjustmentData])

  const onSubmit = async data => {
    const adjustment = formatData(data)
    if (adjustment.materials.length === 0) {
      dispatchSnackbar(dispatch, 'error', 'Please select at least one material')
      return
    }
    if (adjustment.materials.some(material => material.netWeight === 0 && material.netCost === 0)) {
      dispatchSnackbar(dispatch, 'error', 'Please enter a valid WIP or WIP Cost')
      return
    }
    dispatch(setDelayRefetch(true))
    if (viewMode === MODAL_VIEW_MODES.NEW) {
      await createInventoryAdjustment({ inventoryAdjustment: adjustment })
        .unwrap()
        .then(response => handleSuccess(response, 'created'))
        .catch(error => handleError(error, 'creating'))
    } else {
      await updateAdjustment({
        id: adjustmentId,
        inventoryAdjustment: { ...adjustment, id: adjustmentId },
      })
        .unwrap()
        .then(response => handleSuccess(response, 'updated'))
        .catch(error => handleError(error, 'updating'))
    }
  }

  const calculateAdjustment = ({ field, index, value }) => {
    // THIS WILL BE CALLED WHEN THE USER CHANGES THE NEW WIP OR NEW WIP COST
    // 1. GET THE ROW
    // 2. CALCULATE THE NEW VALUES
    // 3. UPDATE THE ROW
    // 4. UPDATE THE FORM VALUES
    const row = apiRef.current?.getRow(apiRef.current?.getRowIdFromRowIndex(index))
    const newValueFloat = parseFloat(value).toFixed(4)
    const newWipFloat = parseFloat(row?.newWip).toFixed(4)
    const wipCostFloat = parseFloat(
      parseNumberObject(row?.wipCost, {
        format: 'none',
        enableFourDecimalPrecisionInInventory: true,
      }),
    ).toFixed(4)
    const wipChange = Number(parseFloat(newValueFloat - parseFloat(row?.wip).toFixed(4)))

    if (field === 'newWip') {
      const newWipCostFloat = parseFloat(row?.newWipCost).toFixed(4)
      const wipCostChange =
        wipChange === 0 ? 0 : Number(parseFloat(newWipCostFloat * newValueFloat - wipCostFloat).toFixed(4))
      apiRef.current.updateRows([
        {
          ...row,
          newWip: newValueFloat,
          wipChange,
          wipCostChange,
        },
      ])
      setValue(`materials.${index}.wipChange`, wipChange, {
        shouldDirty: false,
      })
      setValue(`materials.${index}.wipCostChange`, wipCostChange, {
        shouldDirty: false,
      })
    }
    if (field === 'newWipCost') {
      const wipCostChange =
        wipChange === 0 ? 0 : Number(parseFloat(newValueFloat * newWipFloat - wipCostFloat).toFixed(4))

      const wipAvgCostChange = parseFloat(newValueFloat - parseFloat(row?.wipAvgCost).toFixed(4)).toFixed(4)

      apiRef.current.updateRows([
        {
          ...row,
          newWipCost: newValueFloat,
          wipCostChange,
          wipAvgCostChange,
        },
      ])
      setValue(`materials.${index}.wipCostChange`, wipCostChange, {
        shouldDirty: false,
      })
      setValue(`materials.${index}.wipAvgCostChange`, wipAvgCostChange, {
        shouldDirty: false,
      })
    }
  }

  const getMaterialInventoryAndCalculate = (materialTypeSelected, rowIndex) => {
    // THIS WILL BE CALLED WHEN THE USER SELECTS A MATERIAL TYPE
    // 1. FETCH MATERIALS INVENTORY
    // 2. CALCULATE THE NEW VALUES WITH VALIDATION OF CHANGES IN FORM
    // 3. UPDATE THE ROW
    // 4. UPDATE THE FORM VALUES

    const validDateTime = dayjs(watchEffectiveDate).isValid()
    if (!validDateTime) return
    setIsFetchingInventory(true)
    fetchMaterialsInventory(
      materialsInventory => {
        setIsFetchingInventory(false)
        const currentRow = apiRef.current?.getRow(apiRef.current?.getRowIdFromRowIndex(rowIndex))
        const materialInventory = materialsInventory[0]

        const newWipFloat = parseFloat(materialInventory?.wip?.amount)
        const newWipAvgCostFloat = parseFloat(
          parseNumberObject(materialInventory?.wipAvgCost, {
            format: 'none',
            enableFourDecimalPrecisionInInventory,
          }),
        )
        const wip = Number(materialInventory?.wip?.amount) || 0
        const wipAvgCost = materialInventory?.wipAvgCost
        const wipAvgCostFloat = parseFloat(
          parseNumberObject(wipAvgCost, {
            format: 'none',
            enableFourDecimalPrecisionInInventory,
          }),
        )

        const materialUpdated = {
          ...currentRow,
          materialTypeId: materialInventory?.materialTypeId,
          materialType: {
            commonName: materialInventory?.materialTypeName,
            materialTypeId: materialInventory?.materialTypeId,
            code: materialInventory?.materialTypeCode,
          },
          wip,
          wipCost: materialInventory?.wipCost,
          wipAvgCost: wipAvgCostFloat.toFixed(4),
          newWip: setDecimalPrecision(Number(newWipFloat), {
            decimalPrecisionOverride: 1,
            shouldFormatNumber: false,
            removeTrailingZeros: true,
          }),
          newWipCost: newWipAvgCostFloat,
          wipChange: 0,
          wipCostChange: 0,
          wipAvgCostChange: 0,
        }

        apiRef.current.updateRows([materialUpdated])
        setValue(`materials.${rowIndex}.materialType`, materialUpdated.materialType)
        setValue(`materials.${rowIndex}.materialTypeId`, materialUpdated.materialTypeId)
        setValue(`materials.${rowIndex}.wip`, materialUpdated.wip)
        setValue(`materials.${rowIndex}.wipCost`, materialUpdated.wipCost)
        setValue(`materials.${rowIndex}.wipAvgCost`, materialUpdated.wipAvgCost)
        setValue(`materials.${rowIndex}.wipChange`, materialUpdated.wipChange)
        setValue(`materials.${rowIndex}.wipCostChange`, materialUpdated.wipCostChange)
        setValue(`materials.${rowIndex}.wipAvgCostChange`, materialUpdated.wipAvgCostChange)

        setValue(`materials.${rowIndex}.newWip`, materialUpdated.newWip, {
          shouldDirty: false,
        })
        setValue(`materials.${rowIndex}.newWipCost`, materialUpdated.newWipCost, { shouldDirty: false })
      },
      [materialTypeSelected],
      watchEffectiveDate,
    )
  }

  useEffect(() => {
    // THIS WILL RUN ONCE WHEN THE COMPONENT MOUNTS AND WHEN THE EFFECTIVE DATE CHANGES
    // 1. IF THERE IS AN EFFECTIVE DATE AND THE VIEW MODE IS NOT VIEW, THEN:
    // a. FETCH MATERIALS INVENTORY
    // b. CALCULATE THE NEW VALUES
    // c. UPDATE THE ROWS
    // d. RESET THE MATERIALS OF FORM

    if (watchEffectiveDate && viewMode !== MODAL_VIEW_MODES.VIEW) {
      const materialRows = Array.from(apiRef.current?.getRowModels().values())
      // materialTypeIds could contain undefined values, we need to leave them to keep the same index
      const materialTypeIds = materialRows.map(row => row.materialType?.materialTypeId)
      const hasValues = materialTypeIds.some(value => value)
      if (!hasValues) return

      const validDateTime = dayjs(watchEffectiveDate).isValid()
      if (!validDateTime) return
      setIsFetchingInventory(true)
      fetchMaterialsInventory(
        materialsInventory => {
          setIsFetchingInventory(false)

          const materialsUpdated = materialRows.map(currentRow => {
            const index = currentRow.index
            const materialInventory = materialsInventory.find(
              entry => entry.materialTypeId === currentRow?.materialTypeId,
            )
            const newWipFloat = parseFloat(currentRow?.newWip).toFixed(4)
            const newWipAvgCostFloat = parseFloat(currentRow?.newWipCost).toFixed(4)

            const wip = Number(materialInventory?.wip?.amount) || 0
            const wipFloat = parseFloat(wip).toFixed(4)

            const wipChange = parseFloat(newWipFloat - wipFloat).toFixed(4)

            const wipCostFloat = parseFloat(
              parseNumberObject(materialInventory?.wipCost, {
                format: 'none',
                enableFourDecimalPrecisionInInventory,
              }),
            ).toFixed(4)

            const wipAvgCost = materialInventory?.wipAvgCost
            const wipAvgCostFloat = parseFloat(
              parseNumberObject(wipAvgCost, {
                format: 'none',
                enableFourDecimalPrecisionInInventory,
              }),
            )

            const wipCostChange = Number(parseFloat(newWipAvgCostFloat * newWipFloat - wipCostFloat).toFixed(4))
            const wipAvgCostChangeFloat = parseFloat(newWipAvgCostFloat - wipAvgCostFloat).toFixed(4)
            const newMaterial = {
              ...currentRow,
              id: currentRow?.id,
              materialTypeId: currentRow?.materialTypeId,
              materialType: {
                ...currentRow?.materialType,
                materialTypeId: currentRow?.materialTypeId,
                code: materialInventory?.materialTypeCode,
              },
              wip,
              wipCost: materialInventory?.wipCost,
              wipAvgCost: wipAvgCostFloat.toFixed(4),
              newWip: setDecimalPrecision(Number(newWipFloat), {
                decimalPrecisionOverride: 1,
                shouldFormatNumber: false,
                removeTrailingZeros: true,
              }),
              newWipCost: newWipAvgCostFloat,
              wipChange: Number(wipChange),
              wipCostChange,
              wipAvgCostChange: wipAvgCostChangeFloat,
            }
            setValue(`materials.${index}`, {
              ...getValues(`materials.${index}`),
              wip: newMaterial.wip,
              wipCost: newMaterial.wipCost,
              wipAvgCost: newMaterial.wipAvgCost,
              newWip: newMaterial.newWip,
              newWipCost: newMaterial.newWipCost,
              wipChange: newMaterial.wipChange,
              wipCostChange: newMaterial.wipCostChange,
              wipAvgCostChange: newMaterial.wipAvgCostChange,
            })
            return newMaterial
          })
          apiRef.current.updateRows(materialsUpdated)
        },
        materialTypeIds,
        watchEffectiveDate,
      )
    }
  }, [watchEffectiveDate])

  const columns = useMemo(
    () => [
      {
        field: 'materialType',
        headerName: 'Material',
        flex: 2,
        renderCell: params => {
          const { materialType, index } = params.row
          return (
            <MaterialAutocomplete
              label="Material"
              size="small"
              placeholder="Search by Material Name"
              variant="outlined"
              preventDuplicateMaterials
              apiRef={apiRef}
              InputProps={{ name: `materials.${index}.materialType` }}
              defaultValue={{
                commonName: materialType?.commonName,
                materialTypeId: materialType?.materialTypeId,
                code: materialType?.code,
              }}
              onChange={(value, reason) => {
                if (reason === 'clear') {
                  setError(`materials.${index}.materialType`, {
                    type: 'custom',
                    message: 'Material is required.',
                  })
                  clearMaterialRow(index)
                }
                if (reason === 'selectOption' && value?.defaultPriceEntry?.materialTypeId) {
                  clearErrors(`materials.${index}.materialType`)
                  getMaterialInventoryAndCalculate(value?.defaultPriceEntry?.materialTypeId, index)
                }
              }}
              disabled={viewMode === MODAL_VIEW_MODES.VIEW}
              TextFieldProps={{
                error: !!errors?.materials?.[index]?.materialType,
              }}
            />
          )
        },
      },
      {
        field: 'wip',
        headerName: 'Current WIP',
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        sortable: false,
        renderCell: params => {
          const { wip } = params.row
          return formatNumberForDisplay(wip, { format: 'weight' })
        },
      },
      {
        field: 'newWip',
        headerName: `Adjusted WIP (${defaultYardUm})`,
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        sortable: false,
        renderCell: ({ row }) => {
          const { index, materialType, newWip } = row
          return (
            <EditableCell
              control={control}
              name={`materials.${index}.newWip`}
              disabled={viewMode === MODAL_VIEW_MODES.VIEW}
              defaultValue={newWip}
              isEdit
              size="small"
              onChange={value => {
                if (Number.isNaN(Number(value?.target?.value))) return
                const newValueFloat = value?.target?.value ? parseFloat(value?.target?.value).toFixed(4) : ''
                const oldValueFloat = row?.newWip ? parseFloat(row?.newWip).toFixed(4) : ''
                if (materialType && viewMode !== MODAL_VIEW_MODES.VIEW && newValueFloat !== oldValueFloat) {
                  calculateAdjustment({
                    field: 'newWip',
                    index,
                    value: value?.target?.value,
                  })
                  if (!value?.target?.value) {
                    setError(`materials.${index}.newWip`, {
                      type: 'required',
                      message: 'Actual WIP is required.',
                    })
                  } else {
                    clearErrors(`materials.${index}.newWip`)
                  }
                }
              }}
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  'data-test': 'regrade-actual-wip-input',
                  decimalScale: 1,
                  style: {
                    fontSize: '0.875rem',
                    textAlign: 'right',
                  },
                },
              }}
            />
          )
        },
      },
      {
        field: 'wipCost',
        headerName: 'Current Cost',
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        sortable: false,
        renderCell: params => {
          const { wipCost, wipAvgCost } = params.row
          return (
            <Stack direction="column">
              <Typography variant="body2" align="right">
                {formatNumberForDisplay(wipCost, {
                  format: 'price',
                  decimalPrecisionOverride: 4,
                })}
              </Typography>
              <Typography variant="body2" color="textSecondary" align="right">
                {formatNumberForDisplay(wipAvgCost, {
                  format: 'pricePerUnit',
                  decimalPrecisionOverride: 4,
                })}
              </Typography>
            </Stack>
          )
        },
      },
      {
        field: 'newWipCost',
        headerName: `Adjusted Cost/${defaultYardUm}`,
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        sortable: false,
        renderCell: ({ row }) => {
          const { index, materialType, newWipCost } = row
          return (
            <EditableCell
              control={control}
              name={`materials.${index}.newWipCost`}
              defaultValue={newWipCost}
              isEdit
              size="small"
              disabled={viewMode === MODAL_VIEW_MODES.VIEW}
              onChange={value => {
                if (!value?.target?.value || Number.isNaN(Number(value?.target?.value))) return
                const newValueFloat = value?.target?.value ? parseFloat(value?.target?.value).toFixed(4) : ''
                const oldValueFloat = row?.newWipCost ? parseFloat(row?.newWipCost).toFixed(4) : ''
                if (materialType && viewMode !== MODAL_VIEW_MODES.VIEW && newValueFloat !== oldValueFloat) {
                  calculateAdjustment({
                    field: 'newWipCost',
                    index,
                    value: value?.target?.value,
                  })
                  if (!value?.target?.value) {
                    setError(`materials.${index}.newWipCost`, {
                      type: 'required',
                      message: 'Actual WIP Cost is required.',
                    })
                  } else {
                    clearErrors(`materials.${index}.newWipCost`)
                  }
                }
              }}
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  inputType: 'price',
                  'data-test': 'regrade-actual-wip-input',
                  decimalScale: 4,
                  style: {
                    fontSize: '0.875rem',
                    textAlign: 'right',
                  },
                },
              }}
            />
          )
        },
      },
      {
        field: 'wipChange',
        headerName: 'WIP Change',
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        sortable: false,
        renderCell: params => formatNumberForDisplay(params.row.wipChange, { format: 'weight' }),
      },
      {
        field: 'wipCostChange',
        headerName: 'Cost Change',
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        sortable: false,
        renderCell: params => {
          const { wipCostChange, wipAvgCostChange } = params.row
          return (
            <Stack direction="column">
              <Typography variant="body2" align="right">
                {formatNumberForDisplay(wipCostChange, {
                  format: 'price',
                  decimalPrecisionOverride: 4,
                })}
              </Typography>
              <Typography variant="body2" color="textSecondary" align="right">
                {formatNumberForDisplay(wipAvgCostChange, {
                  format: 'pricePerUnit',
                  decimalPrecisionOverride: 4,
                })}
              </Typography>
            </Stack>
          )
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 1,
        sortable: false,
        headerAlign: 'right',
        align: 'right',
        renderCell: params => {
          const { index, id } = params.row
          const rowsCount = apiRef.current?.getRowsCount()
          return !(index === 0 && rowsCount === 1) && viewMode !== MODAL_VIEW_MODES.VIEW ? (
            <IconButton
              aria-label="delete"
              onClick={() => {
                remove(index)
                apiRef?.current?.updateRows([{ id, _action: 'delete' }])
              }}
            >
              <DeleteIcon />
            </IconButton>
          ) : null
        },
      },
    ],
    [controlledFields, apiRef, viewMode, errors, defaultYardUm],
  )

  const validateCancel = () =>
    !!(touchedFields?.materials?.length > 0) ||
    !!dirtyFields?.effectiveDate ||
    !!dirtyFields?.notes ||
    dirtyFields?.materials?.some(field => field?.materialType?.materialTypeId)

  return (
    <FormProvider {...methods}>
      <AlertModal
        open={showCloseModal}
        modalType="close"
        title="Leave This Page"
        question="Are you sure you want to leave this page?"
        description="You have unsaved data on this page and all unsaved data on the adjustment will be lost."
        confirmLabel="YES, LEAVE PAGE"
        cancelLabel="GO BACK"
        onClose={() => setShowCloseModal(false)}
        onSubmit={() => onModalClose()}
      />
      <AlertModal
        open={showVoidModal}
        modalType="void"
        title="Void Adjustment"
        question="Are you sure you want to void this Inventory Adjustment?"
        description="This action cannot be undone."
        confirmLabel="YES, CONFIRM VOID"
        cancelLabel="GO BACK"
        onClose={() => {
          setShowVoidModal(false)
        }}
        onSubmit={onClickVoid}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        {isLoading && <Loading />}
        <Stack height="100vh">
          <FormModalHeading
            title={adjustmentData ? `Adjustment #${adjustmentData.externalId}` : 'New Adjustment'}
            backButtonPosition="right"
            onBack={() => (validateCancel() ? setShowCloseModal(true) : onModalClose())}
            isInLockedAccountingPeriodV2={isInLockedAccountingPeriodV2}
            type="adjustment"
          />
          <Divider />
          <Stack alignItems="center">
            <Box width="20%" />
          </Stack>
          <Stack p={5} flexGrow={1} overflow="scroll">
            <Box mb={3}>
              <Typography variant="h6" mb={3}>
                Effective Date
              </Typography>
              <Grid container spacing={2}>
                <Grid item md={4} xs={12}>
                  <DateTimePicker
                    name="effectiveDate"
                    label="Effective Date"
                    control={control}
                    variant="outlined"
                    defaultDateTime={dayjs()}
                    dateTimePickerProps={{
                      disabled: viewMode === MODAL_VIEW_MODES.VIEW,
                      size: 'small',
                      'data-test': 'regrade-effective-datetime-picker',
                      timezone: defaultYardTimezone,
                      minDate:
                        viewMode !== MODAL_VIEW_MODES.VIEW && lockAccountingPeriod.isLockedForAccounting
                          ? dayjs(lockAccountingPeriod.lockAccountingDate).add(1, 'day')
                          : undefined,
                    }}
                    error={!!errors?.effectiveDate}
                    helperText={errors?.effectiveDate?.message}
                  />
                </Grid>
              </Grid>
            </Box>
            <Typography variant="h6" mb={2}>
              Adjustments
            </Typography>
            <Box flexGrow={1}>
              <BasicDataGrid
                className={classes.root}
                rows={controlledFields}
                columns={columns}
                apiRef={apiRef}
                disableColumnMenu
                pagination
                pageSize={pageSize}
                onPageSizeChange={newPageSize => setPageSize(newPageSize)}
                rowsPerPageOptions={[10, 20, 30]}
                hideFooterSelectedRowCount
                height="fit-content"
                onCellClick={(_, event) => event.stopPropagation()}
              />
              {viewMode !== MODAL_VIEW_MODES.VIEW ? (
                <Box>
                  <Button
                    startIcon={<Add />}
                    onClick={() => {
                      append(getNewMaterial())
                      trigger()
                    }}
                    data-testid="material-new-button"
                    sx={{
                      width: '100%',
                    }}
                  >
                    Add Material
                  </Button>
                </Box>
              ) : null}
            </Box>
            <Typography variant="h6" mb={2}>
              Additional Information
            </Typography>
            <Grid container direction="row" mb={4}>
              <Grid item md={8} xs={12}>
                <Input
                  name="notes"
                  label="Notes"
                  data-test="notes"
                  variant="outlined"
                  defaultValue=""
                  control={control}
                  disabled={viewMode === MODAL_VIEW_MODES.VIEW}
                />
              </Grid>
            </Grid>
          </Stack>
          <FormModalActionBar
            submitActionText="Confirm Adjustment"
            deleteActionText="Void"
            submitDisabled={
              !isDirty || Object.keys(errors).length > 0 || initialGridValues() || !dayjs(watchEffectiveDate).isValid()
            }
            viewMode={viewMode}
            onCancel={() => (validateCancel() ? setShowCloseModal(true) : onModalClose())}
            onDelete={() => {
              setShowVoidModal(true)
            }}
            onEdit={onEdit}
            isInLockedAccountingPeriodV2={isInLockedAccountingPeriodV2}
            isFormEditable
            isFormVoidable
          />
        </Stack>
      </form>
    </FormProvider>
  )
}

NewAdjustmentContainer.propTypes = {
  onFormStateChange: PropTypes.func,
  onHideModal: PropTypes.func,
}

NewAdjustmentContainer.defaultProps = {
  onFormStateChange: noop,
  onHideModal: null,
}

export default NewAdjustmentContainer
